import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { FiltroPesquisaClientes } from "app/types/cliente/FiltroPesquisaClientes";
import { ICliente } from "app/types/ICliente";
import { IEstado } from "app/types/IEstado";
import { IOrcamento } from "app/types/IOrcamento";
import { IVendedor } from "app/types/IVendedor";
import { Vendedor } from "app/types/Vendedor";
import clsx from "clsx";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IColuna } from "../../types/IColuna";
import { InputCpfCnpj } from "../inputs/InputCpfCnpj";
import { InputTelefoneCelular } from "../inputs/InputTelefoneCelular";
import { Paginacao } from "../itens-orcamento/paginacao/Paginacao";
import { SelecaoComFiltro } from "../selecao-com-filtro/SelecaoComFiltro";
import { Separador } from "../separador/Separador";
import { AcoesHead } from "./TableHead/AcoesHead";
import { HeadersPesquisarCliente } from "./TableHead/HeadersPesquisarCliente";

const estilosPesquisarCliente = require("./pesquisar-cliente.module.scss").default;
const estiloTabela = require('../../estilos/tabelas.module.scss').default
import { fonteGradeOuHerdada } from 'app/utils/tamanhoFonteGrid'
const tamanhoPaginaPadrao = 20;

const estiloCampoNome = { minWidth: "360px" };
const estiloCampoMedio = { maxWidth: "200px" };
const estiloCampoEstado = { maxWidth: "60px" };
const estiloInputCpfCnpj = { height: "26px" };
const estiloEspacadorFinal = { marginBottom: "100px" };
const nomesColunasPadraoPesquisarCliente = [
  "Nome",
  "CPF/CNPJ",
  "Endereço",
  "Cidade",
  "UF",
  "Vend.Relacionado",
  "ClienteAssociado"
];

const obterValorColunaCliente = (nomeColuna: string, item: ICliente) => {
  switch (nomeColuna) {
    case "Nome":
      return item.Nome;
    case "CPF/CNPJ":
      return item.CpfCnpj;
    case "Endereço":
      return item.Endereco;
    case "Bairro":
      return item.Bairro;
    case "Cidade":
      return item.Cidade?.Nome;
    case "UF":
      return item.SiglaUf;
    case "Telefone":
      return item.Telefone;
    case "Vend.Relacionado":
      return `${item.CodVendRelacionado ?? ""} - ${item.NomeVendedorRelacionado ?? ""}`;
    case "ClienteAssociado":
      return item.ClienteAssociado;
    default:
      return "";
  }
};

const obterChaveCliente = (cliente: ICliente, indice: number) =>
  cliente.Codigo?.toString() ?? `${cliente.CpfCnpj ?? cliente.Nome ?? "SemCodigo"}_${indice}`;

const obterChaveColuna = (coluna: IColuna, indice?: number) =>
  coluna.Codigo?.toString() ?? coluna.Nome ?? indice?.toString() ?? "";

const obterChaveEstado = (estado: IEstado, indice: number) =>
  estado.Codigo?.toString() ?? estado.Sigla ?? indice.toString();

const obterClientesTabela = (dadosClienteTabela?: Partial<PagedMessageResponse<ICliente>>) =>
  Array.isArray(dadosClienteTabela?.Entities) ? dadosClienteTabela.Entities : [];

const obterColunasPadraoPesquisarCliente = (exibirVendedorRelacionado?: boolean) =>
  nomesColunasPadraoPesquisarCliente
    .filter(nomeColuna => exibirVendedorRelacionado || nomeColuna !== "Vend.Relacionado")
    .map((nomeColuna, indice) => ({
      Codigo: indice + 1,
      Nome: nomeColuna,
      Selecionada: true,
      Permitido: true
    } as IColuna));

const obterColunasGridPesquisarCliente = (colunas?: IColuna[], exibirVendedorRelacionado?: boolean) => {
  const colunasPermitidas = Array.isArray(colunas)
    ? colunas.filter(coluna => coluna.Permitido !== false)
    : [];

  const colunasGrid = colunasPermitidas.length > 0
    ? colunasPermitidas
    : obterColunasPadraoPesquisarCliente(exibirVendedorRelacionado);

  if (colunasGrid.some(coluna => coluna.Selecionada)) {
    return colunasGrid;
  }

  return colunasGrid.map(coluna => ({ ...coluna, Selecionada: true }));
};

export type PesquisarClienteProps = {
  foraOrcamento?: boolean
  vendedorLogado: Vendedor
  permiteEngrenagemGrid: boolean
  processandoPesquisarCliente: boolean
  filtrosPesquisaCliente?: FiltroPesquisaClientes;
  dadosClienteTabela?: Partial<PagedMessageResponse<ICliente>>;
  listaEstados?: IEstado[];
  exibirVendedorRelacionado?: boolean;
  alterarVendedorRelacionado: boolean
  visualizaApenasClientesVendedorRelacionado?: boolean
  orcamento: string
  somenteLeitura: boolean
  mostrarBotoes: boolean
  mostrarBotaoNovo?: boolean;
  mostrarBotaoNaoCadastrado?: boolean;
  colunas: IColuna[];
  tamanhoFontePadrao?: number;
  listaVendedores?: IVendedor[];
  onNovoCliente?: () => void;
  onClienteNaoCadastrado?: () => void;
  onPesquisar?: (filtroPesquisaCliente: FiltroPesquisaClientes) => void;
  onPesquisarVendedorRelacionado: (nomeVendedorRelacionado: string) => void;
  onToggleColunaSelecionada: (nomeColuna: string, selecionada: boolean) => void;
  onToggleSelecionarTodasColunas: (selecionar: boolean) => void;
  onAlterarFonte?: (tamanho: number) => void;
  onSelecionarCliente?: (cliente: ICliente) => void;
  onClienteModificado: (filtroPesquisaCliente: FiltroPesquisaClientes) => void;
};

export const PesquisarCliente = React.memo((props: PesquisarClienteProps) => {
  const [numeroPagina, setNumeroPagina] = useState(1)

  const vendedorRelacionadoFixo = useMemo(() => {
    return Boolean(props.exibirVendedorRelacionado && props.vendedorLogado?.CodigoPessoa && !props.alterarVendedorRelacionado);
  }, [props.alterarVendedorRelacionado, props.exibirVendedorRelacionado, props.vendedorLogado?.CodigoPessoa]);

  const valoresIniciais = useMemo<FiltroPesquisaClientes>(() => ({
    ...props.filtrosPesquisaCliente,
    Uf: props.filtrosPesquisaCliente?.Uf || "",
    OidVendedorRelacionado: vendedorRelacionadoFixo && props.visualizaApenasClientesVendedorRelacionado ? props.vendedorLogado.CodigoPessoa.toString() : null
  }), [props.filtrosPesquisaCliente, props.vendedorLogado?.CodigoPessoa, vendedorRelacionadoFixo, props.visualizaApenasClientesVendedorRelacionado]);

  const formik = useFormik({
    initialValues: valoresIniciais,
    onSubmit: (values) => {
      setNumeroPagina(1);
      props.onPesquisar?.({ ...values, NumeroPagina: 1, TamanhoPagina: tamanhoPaginaPadrao });
    },
    enableReinitialize: true,
  });

  const desabilitarFormulario = props.somenteLeitura || props.processandoPesquisarCliente;

  const colunasGrid = useMemo(
    () => obterColunasGridPesquisarCliente(props.colunas, props.exibirVendedorRelacionado),
    [props.colunas, props.exibirVendedorRelacionado]
  );

  const colunasSelecionadas = useMemo(
    () => colunasGrid.filter(c => c.Selecionada),
    [colunasGrid]
  );

  const clientes = obterClientesTabela(props.dadosClienteTabela);

  const listaVendedores = useMemo(() => props.listaVendedores ?? [], [props.listaVendedores]);

  const vendedoresPorCodigo = useMemo(() => {
    return new Map(listaVendedores.map(vendedor => [vendedor.Codigo.toString(), vendedor]));
  }, [listaVendedores]);

  const itensVendedores = useMemo(() => (
    listaVendedores.map(vendedor => ({
      chave: vendedor.Codigo.toString(),
      valor: vendedor.Nome
    }))
  ), [listaVendedores]);

  const opcoesEstado = useMemo(() => (
    props.listaEstados?.map((value, index) => (
      <option
        key={`PesquisaCliente_Estado_${obterChaveEstado(value, index)}`}
        value={value.Sigla}
        id={`PesquisaCliente_Estado_${obterChaveEstado(value, index)}`}
      >
        {value.Sigla}
      </option>
    ))
  ), [props.listaEstados]);

  const itemSelecionadoVendedorRelacionado = useMemo(() => {
    if (vendedorRelacionadoFixo) {
      return {
        chave: props.vendedorLogado.CodigoPessoa.toString(),
        valor: props.vendedorLogado?.Nome
      };
    }

    const vendedorSelecionado = vendedoresPorCodigo.get(formik.values.OidVendedorRelacionado);

    return {
      chave: formik.values.OidVendedorRelacionado,
      valor: vendedorSelecionado?.Nome
    };
  }, [
    formik.values.OidVendedorRelacionado,
    props.vendedorLogado?.CodigoPessoa,
    props.vendedorLogado?.Nome,
    vendedoresPorCodigo,
    vendedorRelacionadoFixo
  ]);

  const estiloLinha = useMemo(
    () => ({ fontSize: fonteGradeOuHerdada(props.tamanhoFontePadrao) }),
    [props.tamanhoFontePadrao]
  );

  const handleCpfCnpjModificado = useCallback((novoCpfCnpj: string) => {
    formik.setFieldValue("CpfCnpj", novoCpfCnpj);
  }, [formik.setFieldValue]);

  const handlePesquisarVendedorRelacionado = useCallback((vendedor: string) => {
    props.onPesquisarVendedorRelacionado(vendedor);
  }, [props.onPesquisarVendedorRelacionado]);

  const handleVendedorSelecionado = useCallback((item) => {
    formik.setFieldValue("OidVendedorRelacionado", vendedoresPorCodigo.get(item.chave)?.Codigo.toString());
  }, [formik.setFieldValue, vendedoresPorCodigo]);

  const handleNovoCliente = useCallback(() => {
    props.onNovoCliente?.();
  }, [props.onNovoCliente]);

  const handleClienteNaoCadastrado = useCallback(() => {
    props.onClienteNaoCadastrado?.();
  }, [props.onClienteNaoCadastrado]);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    formik.handleSubmit();
  }, [formik.handleSubmit]);

  const handleAlterarPagina = useCallback((numero: number) => {
    setNumeroPagina(numero);
    props.onPesquisar?.({ ...formik.values, NumeroPagina: numero });
  }, [formik.values, props.onPesquisar]);

  const handleSelecionarCliente = useCallback((cliente: ICliente) => {
    props.onSelecionarCliente?.(cliente);
  }, [props.onSelecionarCliente]);

  useEffect(() => {
    props.onClienteModificado(formik.values)
  }, [formik.values, props.onClienteModificado])

  const itensPesquisarCliente = useCallback(({ linha, nomeColuna, item, chaveCliente }: {
    linha: number, nomeColuna: string, item: ICliente, chaveCliente: string
  }) => {
    return <div className={`_table-cell ${estilosPesquisarCliente.tableCellBody}`}
      id={`PesquisarCliente_Coluna_${nomeColuna}_${linha}`}
      key={`PesquisarCliente_Coluna_${nomeColuna}_${chaveCliente}`}
    >{obterValorColunaCliente(nomeColuna, item)}</div>
  }, [])

  const campoNome = <>
    <label>Nome</label>
    <input
      style={estiloCampoNome}
      name="Nome"
      type="text"
      maxLength={100}
      autoComplete="off"
      id="PesquisaCliente_CampoNome"
      accessKey="o"
      disabled={desabilitarFormulario}
      autoFocus
      value={formik.values?.Nome ?? ""}
      onChange={formik.handleChange}
    />
  </>

  const campoApelido = <>
    <label>Apelido</label>
    <input
      style={estiloCampoMedio}
      name="Apelido"
      type="text"
      maxLength={100}
      autoComplete="off"
      id="PesquisaCliente_CampoApelido"
      disabled={desabilitarFormulario}
      value={formik.values?.Apelido ?? ""}
      onChange={formik.handleChange}
    />
  </>

  const campoCnpjCpf = <>
    <label>CNP<u>J</u>/CPF</label>
    <div className={estilosPesquisarCliente.containerInputCpfCnpj}>
      <InputCpfCnpj
        id="PesquisaCliente_CpfCnpj"
        name="CpfCnpj"
        teclaDeAtalho="j"
        style={estiloInputCpfCnpj}
        disabled={desabilitarFormulario}
        emitirComMascara={false}
        cpfCnpj={formik.values?.CpfCnpj ?? ""}
        onCpfCnpjModificado={handleCpfCnpjModificado}
      />
    </div>
  </>

  const campoInscricaoEstadual = <>
    <label>Inscrição Estadual</label>
    <input
      style={estiloCampoMedio}
      name="InscricaoEstadual"
      type="text"
      maxLength={20}
      disabled={desabilitarFormulario}
      value={formik.values?.InscricaoEstadual ?? ""}
      onChange={formik.handleChange}
    />
  </>

  const campoCidade = <>
    <label>Cidade</label>
    <input
      style={estiloCampoMedio}
      name="Cidade"
      type="text"
      autoComplete="off"
      id="PesquisaCliente_CampoCidade"
      maxLength={60}
      disabled={desabilitarFormulario}
      value={formik.values?.Cidade ?? ""}
      onChange={formik.handleChange}
    />
  </>

  const campoEstado = <>
    <label>Estado</label>
    <select
      style={estiloCampoEstado}
      name="Uf"
      disabled={desabilitarFormulario}
      value={formik.values?.Uf ?? ""}
      multiple={false}
      onChange={formik.handleChange}
    >
      <option value={""} id="PesquisaCliente_Estado_Vazio"></option>
      {opcoesEstado}
    </select>
  </>

  const campoBairro = <>
    <label>Bairro</label>
    <input
      style={{ maxWidth: "200px" }}
      name="Bairro"
      type="text"
      autoComplete="off"
      id="PesquisaCliente_CampoBairro"
      maxLength={60}
      disabled={props.somenteLeitura || props.processandoPesquisarCliente}
      value={formik.values?.Bairro ?? ""}
      onChange={formik.handleChange}
    />
  </>

  const campoTelefone = <>
    <label title="Busca telefone fixo ou celular">Telefone</label>
    <InputTelefoneCelular
      id="PesquisaCliente_CampoTelefone"
      style={{ maxWidth: "20ch" }}
      disabled={props.somenteLeitura || props.processandoPesquisarCliente}
      emitirComMascara={false}
      telefoneCelular={formik.values?.Telefone ?? ""}
      onTelefoneCelularModificado={(novoValor) => formik.setFieldValue("Telefone", novoValor)}
    />
  </>

  const campoVendedorRelacionado = <>
    <label
    title="Veja parâmetro:&#013;-Visualiza vendedor relacionado na consulta do cliente?&#013;-Visualiza apenas os clientes do vendedor relacionado?&#013;-Permite alterar o vendedor relacionado, na consulta de clientes?"
    >Vendedor Relacionado</label>

    <SelecaoComFiltro
      itens={itensVendedores}
      id="PesquisaCliente_VendedorRelacionado"
      onValorModificado={handlePesquisarVendedorRelacionado}
      onItemSelecionado={handleVendedorSelecionado}
      disabled={desabilitarFormulario || (props.alterarVendedorRelacionado != null && !props.alterarVendedorRelacionado)}
      itemSelecionado={itemSelecionadoVendedorRelacionado}
      filtroManual={true}
      placeholder="Digite 1 ou mais caracteres"
    />
  </>

  const botoesFormulario = <>
    <button type="submit"
      title={props.foraOrcamento ? "" : "Veja parâmetro: Bloquear a inclusão de orçamento sem cliente?"}
      disabled={desabilitarFormulario}
      id="PesquisaCliente_BarraFerramenta_BotaoPesquisar"
    >Pesquisar</button>
    {props.mostrarBotoes != null && !props.somenteLeitura && (
        <>
          {props.mostrarBotaoNovo && (
            <button onClick={handleNovoCliente}
              disabled={props.processandoPesquisarCliente}
              id="PesquisaCliente_BarraFerramenta_BotaoNovo"
            >Novo</button>
          )}
          {props.mostrarBotaoNaoCadastrado && !props.foraOrcamento && props.orcamento != undefined &&  (
            <button onClick={handleClienteNaoCadastrado}
              disabled={props.processandoPesquisarCliente}
              title="Veja parâmetro:&#013;-Incluir cliente novo somente no orçamento confirmado"
              id="PesquisaCliente_BarraFerramenta_BotaoClienteNaoCadastrado">
              Cliente Não Cadastrado
            </button>
          )}
        </>
      )}
  </>

  return (
    <>
      <form onSubmit={handleSubmit}>
        <fieldset>
          <div>
            <Separador />

            <div className={`_grid _grid-cols-2 _gap-y-2 _float-left ${estilosPesquisarCliente.formularioPesquisaCliente}`}>
              {campoNome}

              {campoApelido}

              {campoCnpjCpf}

              {campoInscricaoEstadual}

              {campoCidade}

              {campoEstado}

              {campoBairro}

              {campoTelefone}

              {props.exibirVendedorRelacionado &&
                campoVendedorRelacionado
              }

            </div>
          </div>
        </fieldset>
        <br />
        <div className={estilosPesquisarCliente.botoesPesquisaCliente}>
          {botoesFormulario}
        </div>
      </form>
      {((props.dadosClienteTabela?.PageCount ?? 0) > 0 || clientes.length > 0) && (
        <>
          <Separador />

          <div className={clsx("_mb-2")} id="Cliente_PesquisaCliente">

            <AcoesHead
              onToggleColunaSelecionada={props.onToggleColunaSelecionada}
              onAlterarFonte={props.onAlterarFonte}
              tamanhoFonte={props.tamanhoFontePadrao}
              colunas={colunasGrid}
              onToggleSelecionarTodasColunas={props.onToggleSelecionarTodasColunas}
              permiteEngrenagemGrid={props.permiteEngrenagemGrid}
            />
            <div className={estiloTabela.containerRolavel}
              id="PesquisarCliente_TabelaContainer"
            >
              <div className={`_table _w-full _border-collapse ${estilosPesquisarCliente.tabelaPesquisaCliente}`}
                id="PesquisarCliente_Tabela"
              >

                <HeadersPesquisarCliente colunas={colunasSelecionadas} />

                <div className="_table-row-group"
                  id="PesquisarCliente_TabelaCorpo"
                >

                  {clientes.map((value, indexLinha) => {
                    const chaveCliente = obterChaveCliente(value, indexLinha);

                    return <div className={`_table-row ${estilosPesquisarCliente.linha}`}
                      style={estiloLinha}
                      key={`PesquisarCliente_Linha_${chaveCliente}`}
                      id={`PesquisarCliente_Linha_${indexLinha}`}
                      onClick={() => handleSelecionarCliente(value)}>

                      {colunasSelecionadas.map((coluna, indexColuna) => (
                        itensPesquisarCliente({
                          linha: indexLinha,
                          nomeColuna: coluna.Nome ?? "",
                          item: value,
                          chaveCliente: `${chaveCliente}_${obterChaveColuna(coluna, indexColuna)}`
                        }))
                      )}

                    </div>
                  })}

                </div>
              </div>
            </div>


            {props.dadosClienteTabela?.PageCount > 1 && (
              <Paginacao
                id="PesquisarCliente_Paginacao"
                paginaAtual={numeroPagina}
                desabilitarPaginacao={props.processandoPesquisarCliente}
                totalPaginas={props.dadosClienteTabela?.PageCount}
                onAlterarListaSelecionada={handleAlterarPagina}
              />
            )}
          </div>
        </>
      )}
      {<section style={estiloEspacadorFinal} />}
    </>
  );
});
